import React, { useState } from 'react';

const BusinessInformation = ({ data }) => {
    const [previewUrl, setPreviewUrl] = useState(null);
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [previewType, setPreviewType] = useState(null);

    if (!data) return null;

    const handlePreview = (url) => {
        setPreviewUrl(url);
        // Determine the type of document based on the URL
        const fileType = url.split('.').pop().toLowerCase();
        setPreviewType(['jpg', 'jpeg', 'png'].includes(fileType) ? 'image' : 'pdf');
        setIsPreviewOpen(true);
    };

    const PreviewModal = ({ url, type, onClose }) => {
        if (!url) return null;

        return (
            <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 flex items-center justify-center p-4">
                <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] flex flex-col">
                    <div className="flex justify-between items-center p-4 border-b">
                        <h3 className="text-lg font-semibold">Document Preview</h3>
                        <button
                            onClick={onClose}
                            className="text-gray-500 hover:text-gray-700"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                    <div className="flex-1 overflow-auto p-4">
                        {type === 'image' ? (
                            <img
                                src={url}
                                alt="Document preview"
                                className="max-w-full h-auto mx-auto"
                            />
                        ) : (
                            <iframe
                                src={url}
                                title="Document preview"
                                className="w-full h-[calc(90vh-8rem)]"
                            />
                        )}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="bg-white rounded-lg shadow-sm">
            <h2 className="text-xl font-semibold mb-6 text-gray-800">Business Information</h2>
            
            <div className="space-y-6">
                {/* Business Registration Document */}
                {data.business_registration && (
                    <div className="space-y-2">
                        <h3 className="text-sm font-medium text-gray-500">Business Registration</h3>
                        <div className="border rounded-lg p-4">
                            <button 
                                onClick={() => handlePreview(data.business_registration)}
                                className="flex items-center text-brand-600 hover:text-brand-700"
                            >
                                <svg 
                                    className="w-5 h-5 mr-2" 
                                    fill="none" 
                                    stroke="currentColor" 
                                    viewBox="0 0 24 24"
                                >
                                    <path 
                                        strokeLinecap="round" 
                                        strokeLinejoin="round" 
                                        strokeWidth="2" 
                                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                    />
                                </svg>
                                View Business Registration Document
                            </button>
                        </div>
                    </div>
                )}

                {/* Verification Document */}
                {data.verification_document && (
                    <div className="space-y-2">
                        <h3 className="text-sm font-medium text-gray-500">Verification Document</h3>
                        <div className="border rounded-lg p-4">
                            <button 
                                onClick={() => handlePreview(data.verification_document)}
                                className="flex items-center text-brand-600 hover:text-brand-700"
                            >
                                <svg 
                                    className="w-5 h-5 mr-2" 
                                    fill="none" 
                                    stroke="currentColor" 
                                    viewBox="0 0 24 24"
                                >
                                    <path 
                                        strokeLinecap="round" 
                                        strokeLinejoin="round" 
                                        strokeWidth="2" 
                                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                    />
                                </svg>
                                View Verification Document
                            </button>
                        </div>
                    </div>
                )}

                {/* Assigned Categories */}
                {data.assigned_categories_details && data.assigned_categories_details.length > 0 && (
                    <div className="space-y-2">
                        <h3 className="text-sm font-medium text-gray-500">Assigned Categories</h3>
                        <div className="flex flex-wrap gap-2">
                            {data.assigned_categories_details.map((category) => (
                                <span 
                                    key={category.id}
                                    className="px-3 py-1 bg-brand-50 text-brand-700 rounded-full text-sm"
                                >
                                    {category.name}
                                </span>
                            ))}
                        </div>
                    </div>
                )}

                {/* Verification Status */}
                <div className="space-y-2">
                    <h3 className="text-sm font-medium text-gray-500">Account Status</h3>
                    <div className="flex items-center space-x-2">
                        {data.is_verified ? (
                            <>
                                <span className="w-2 h-2 bg-brand-500 rounded-full"></span>
                                <span className="text-brand-700">Verified Account</span>
                            </>
                        ) : (
                            <>
                                <span className="w-2 h-2 bg-yellow-500 rounded-full"></span>
                                <span className="text-yellow-700">Pending Verification</span>
                            </>
                        )}
                    </div>
                </div>

                {/* Additional Details */}
                <div className="space-y-2">
                    <h3 className="text-sm font-medium text-gray-500">Account Type</h3>
                    <p className="text-gray-700 capitalize">{data.user_type}</p>
                </div>

                {/* Last Updated */}
                <div className="space-y-2">
                    <h3 className="text-sm font-medium text-gray-500">Last Updated</h3>
                    <p className="text-gray-700">
                        {new Date(data.updated_at).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                        })}
                    </p>
                </div>
            </div>

            {/* Preview Modal */}
            {isPreviewOpen && (
                <PreviewModal
                    url={previewUrl}
                    type={previewType}
                    onClose={() => {
                        setIsPreviewOpen(false);
                        setPreviewUrl(null);
                    }}
                />
            )}
        </div>
    );
};

export default BusinessInformation; 